import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * useAppFlags — subscribes every page to the super-admin controlled
 * feature flags. Flags default to `true` while loading so pages don't
 * flash-hide sections on first paint. Auto-refreshes when the admin
 * fires a `creatoriq:app-flags-changed` custom event.
 *
 * v149.64 — One request for the whole page. Every component using this
 * hook used to fetch `/app-flags` itself, so a bio page with a dozen
 * blocks made 10–20 identical calls at once (each 1–5 s through the
 * origin, holding up the images and calls the page really needed). The
 * flags now live in one module-level store: the first caller fetches,
 * callers mounting meanwhile share that request, later callers get the
 * cached value at once. The admin's change event refetches once for all.
 */
/**
 * v149.54 — The admin's Digital Insights switch (Admin → Feature Flags →
 * "Digital Insights section"). Besides the Dashboard section it gates the
 * Workspace page (connected Instagram / Facebook accounts): the Settings
 * link, the menu item, the demo banner, and the route itself (AppShell).
 */
export const DIGITAL_INSIGHTS_FLAG = "dashboard_digital_insights";

const store = { flags: {}, loaded: false, inflight: null, listeners: new Set() };

function notify() {
  for (const l of store.listeners) l({ flags: store.flags, loaded: store.loaded });
}

function loadFlags() {
  if (store.inflight) return store.inflight;
  store.inflight = api.get("/app-flags")
    .then(({ data }) => { store.flags = data?.flags || {}; })
    .catch(() => { /* leave what we have */ })
    .finally(() => { store.loaded = true; store.inflight = null; notify(); });
  return store.inflight;
}

if (typeof window !== "undefined") {
  window.addEventListener("creatoriq:app-flags-changed", () => { void loadFlags(); });
}

export function useAppFlags() {
  const [state, setState] = useState({ flags: store.flags, loaded: store.loaded });

  useEffect(() => {
    store.listeners.add(setState);
    if (store.loaded) setState({ flags: store.flags, loaded: true });
    else void loadFlags();
    return () => { store.listeners.delete(setState); };
  }, []);

  // `flag(key)` returns true when the flag is missing (default-on),
  // false only when explicitly set to `false`.
  const flag = (key) => state.flags[key] !== false;

  return { flags: state.flags, flag, loaded: state.loaded };
}
